/**
 * First / last name of a person (owner decision 2026-09-30, ADR-020): stored as entered ("Tên",
 * "Họ"); the Vietnamese full name is last + " " + first and must be the name on the identity
 * document. Nothing here splits a full name.
 */

import type { OrgKind, SubjectDraft } from './subjectDraft.ts';

const words = (s: string) => s.split(/\s+/).filter(Boolean);

/** The Vietnamese full name: last + " " + first, single spaces. */
export function fullName(last: string, first: string): string {
  return [...words(last), ...words(first)].join(' ');
}

/** What an edit of an individual changes. */
export interface IdentityEdit {
  firstName: string;
  lastName: string;
  /** A new document number typed (empty: the one on file is kept). */
  idValue: string;
  /** A new birth date typed (empty: the one on file is kept). */
  birthDate: string;
}

/** Whether the edit of an individual changes what its eKYC proved — the legal name, the document
 * number or the date of birth (the server's `same_identity`, crates/subject/src/subjects.rs) — so
 * the new version needs a new eKYC (otherwise it keeps it). */
export function identityChanged(d: IdentityEdit, currentLegalName: string): boolean {
  if (d.idValue.trim() || d.birthDate) return true;
  return fullName(d.lastName, d.firstName).toLocaleLowerCase('vi') !== words(currentLegalName).join(' ').toLocaleLowerCase('vi');
}

/** The fields of the form an organisation's subject declaration declares
 * (config/declarations/vn-subject-organization.yaml, vn-subject-household-business.yaml): name,
 * codes, address, phone, e-mail; an enterprise's trade name and legal representative; a household
 * business's owner (Họ / Tên, CCCD). An enterprise's contact person is not declared. */
const DECLARED: Record<OrgKind, readonly (keyof SubjectDraft)[]> = {
  ENTERPRISE: ['legalName', 'tradeName', 'idValue', 'country', 'street', 'provinceId', 'wardId', 'city', 'state', 'email', 'phone', 'repMode'],
  HOUSEHOLD_BUSINESS: ['legalName', 'idValue', 'householdCode', 'householdTaxCode', 'firstName', 'lastName', 'country', 'street', 'provinceId', 'wardId', 'city', 'state', 'email', 'phone'],
};

/** The notices of an edit (`subjectForm.<notice>`). */
export const VERIFICATION_NOTICES = ['keepVerificationNotice', 'reverifyNotice', 'keepDeclarationNotice', 'resignDeclarationNotice'] as const;
export type VerificationNotice = (typeof VERIFICATION_NOTICES)[number];

/**
 * Whether the edit `d` of the stored subject (`before`, its draft) keeps the current verification,
 * by the server's rule: an individual's eKYC while legal name, document and birth date stay the
 * same ([`identityChanged`]); an organisation's subject declaration while every declared value
 * stays the same (crates/dossier-core/src/subject_declaration.rs).
 */
export function verificationNotice(d: SubjectDraft, before: SubjectDraft): VerificationNotice {
  if (d.kind === 'INDIVIDUAL') return identityChanged(d, before.legalName) ? 'reverifyNotice' : 'keepVerificationNotice';
  // ponytail: values compared as typed (the e-mail lowercased like the server); a phone retyped in
  // another format reads as changed, so the notice may ask to sign again where the server keeps it.
  const value = (x: SubjectDraft, k: keyof SubjectDraft) => (k === 'email' ? x.email.trim().toLowerCase() : String(x[k]).trim());
  return DECLARED[d.orgKind].every((k) => value(d, k) === value(before, k)) ? 'keepDeclarationNotice' : 'resignDeclarationNotice';
}
